import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { subscriptionApi } from '../../lib/subscriptionApi';
import { supabase } from '../../lib/supabase';
import RequestList from './RequestList';

const Dashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCancelMessage, setShowCancelMessage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Stripe success/cancel URLs
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    console.log('Dashboard URL params:', { success, sessionId, canceled });

    if (success === 'true' && sessionId) {
      console.log('Processing successful payment with session ID:', sessionId);
      setShowSuccessMessage(true);
      // Handle successful payment
      handleSuccessfulPayment(sessionId);
    } else if (canceled === 'true') {
      console.log('Payment was canceled');
      setShowCancelMessage(true);
    }
  }, [searchParams]);

  const handleSuccessfulPayment = async (sessionId: string) => {
    try {
      console.log('Handling successful payment for session:', sessionId);
      
      // First, refresh the user session to ensure they're still logged in
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !currentUser) {
        console.error('User not authenticated after payment:', authError);
        // Redirect to login with a message
        navigate('/login?message=Please sign in to complete your subscription');
        return;
      }

      console.log('User authenticated:', currentUser.id);
      
      // Handle the successful payment
      const result = await subscriptionApi.handleSuccessfulPayment(sessionId);
      
      if (result.success) {
        console.log('Payment handled successfully');
        await refreshSubscription();
      } else {
        console.error('Failed to handle payment:', result.error);
      }
    } catch (error) {
      console.error('Error handling successful payment:', error);
    }
  };

  const handleManageSubscription = async () => {
    if (subscription?.stripe_customer_id) {
      try {
        await subscriptionApi.createPortalSession(subscription.stripe_customer_id);
      } catch (error) {
        console.error('Error creating portal session:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Cancel Messages */}
        {showSuccessMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Payment Successful!
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Your subscription has been activated. You can now create design requests.</p>
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setShowSuccessMessage(false)}
                  className="text-green-400 hover:text-green-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {showCancelMessage && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Payment Canceled
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Your payment was canceled. You can try again or contact support if you need help.</p>
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setShowCancelMessage(false)}
                  className="text-yellow-400 hover:text-yellow-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Welcome back, {user?.user_metadata?.full_name || user?.email}!
          </p>
          
          {/* Subscription Status */}
          {subscription && (
            <div className="mt-4 bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {subscription.plan?.name} Plan
                  </h3>
                  <p className="text-sm text-gray-600">
                    Status: <span className={`font-medium ${subscription.status === 'active' ? 'text-green-600' : subscription.status === 'past_due' ? 'text-yellow-600' : subscription.status === 'unpaid' ? 'text-red-600' : 'text-gray-600'}`}>
                      {subscription.status}
                    </span>
                  </p>
                  {subscription.current_period_end && (
                    <p className="text-sm text-gray-600">
                      Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                  
                  {/* Payment Failure Warning */}
                  {(subscription.status === 'past_due' || subscription.status === 'unpaid') && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-red-800">
                            Payment Failed
                          </h4>
                          <div className="mt-1 text-sm text-red-700">
                            <p>
                              {subscription.status === 'past_due' 
                                ? 'Your payment was unsuccessful. Please update your payment method to continue your subscription.'
                                : 'Your subscription has been suspended due to payment failure. Please update your payment method to reactivate.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-2">
                  {subscription.stripe_customer_id && (
                    <button
                      onClick={handleManageSubscription}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        subscription.status === 'active' 
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {subscription.status === 'active' ? 'Manage Subscription' : 'Update Payment Method'}
                    </button>
                  )}
                  
                  {/* Show warning for past_due subscriptions */}
                  {subscription.status === 'past_due' && (
                    <p className="text-xs text-yellow-600 text-center">
                      ⚠️ Your subscription will be suspended soon
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Requests Section */}
        <div className="space-y-6">
          <RequestList />
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 